import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "../modern-ui/sonner";
import heic2any from "heic2any";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import {
  addUserGalleryImage,
  deleteUserGalleryImage,
  fetchUserGallery,
} from "../../store/slices/model/asyncThunks";
import {
  selectAddUserGalleryImagePending,
  selectDeleteUserGalleryImagePending,
  selectUserGallery,
  selectUserGalleryMaxReached,
  selectUserGalleryPage,
  selectUserGalleryPending,
} from "../../store/slices/model/slice";
import { MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } from "../../utils/constants";
import {
  Cost,
  EmptySelectionIndicator,
  EmptyState,
  EmptyStateContent,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
  Footer,
  FooterIconButton,
  GenerateButton,
  GenerateButtonText,
  Grid,
  GridViewport,
  Header,
  HeaderActions,
  IconButton,
  ImageTile,
  MediaIconWrapper,
  ModalContainer,
  ModalContent,
  SelectionIndicator,
  SkeletonTile,
  TileImage,
  TileVideo,
  Title,
  UploadLabel,
  UploadTile,
  UploadingOverlay,
} from "./UserGalleryModal.styles";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as ImageIcon } from "../../assets/icons/image.svg";
import { ReactComponent as ImageUserPlusSvg } from "../../assets/icons/imageUserPlus.svg";
import { ReactComponent as MagicStickSvg } from "../../assets/icons/magicStick.svg";
import { ReactComponent as SettingsSvg } from "../../assets/icons/settings.svg";
import { ReactComponent as TrashSvg } from "../../assets/icons/trash.svg";
import { ReactComponent as VideoRecorderIcon } from "../../assets/icons/video-recorder.svg";
import GradientStarIcon from "../GradientStarIcon/GradientStarIcon";

const MAX_SELECTED_IMAGES = 6;
const DEFAULT_GENERATE_PRICE = 50;
const MAX_UPLOAD_BATCH = 5;
const EMPTY_SELECTED_IMAGES = [];
const SUPPORTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
];
const SUPPORTED_VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const SUPPORTED_VIDEO_EXTENSIONS = [".mp4", ".mov"];
const BLOCKED_VIDEO_EXTENSIONS = [".quicktime"];
const VIDEO_FILE_ACCEPT = SUPPORTED_VIDEO_EXTENSIONS;

const getBaseUrl = (url) => url?.split("?")[0] || "";
const hasSupportedVideoExtension = (value = "") => {
  const normalizedValue = value.toLowerCase();

  return SUPPORTED_VIDEO_EXTENSIONS.some((extension) =>
    normalizedValue.endsWith(extension),
  );
};

const hasBlockedVideoExtension = (value = "") => {
  const normalizedValue = value.toLowerCase();

  return BLOCKED_VIDEO_EXTENSIONS.some((extension) =>
    normalizedValue.endsWith(extension),
  );
};

const isVideoUrl = (url) => {
  return hasSupportedVideoExtension(getBaseUrl(url));
};

const getGalleryMediaItem = (item) => {
  if (!item) {
    return null;
  }

  if (typeof item === "string") {
    return { url: item, type: isVideoUrl(item) ? "video" : "image" };
  }

  if (item.video?.url) {
    return { url: item.video.url, type: "video" };
  }

  const imageUrl = item.image?.url || item.url || null;

  return {
    url: imageUrl,
    type: isVideoUrl(imageUrl) ? "video" : "image",
  };
};

const createPendingUpload = (file) => ({
  id: `${file.name}-${file.size}-${Date.now()}`,
  previewUrl: URL.createObjectURL(file),
  type:
    file.type.startsWith("video/") || hasSupportedVideoExtension(file.name)
      ? "video"
      : "image",
});

const isSupportedVideoUpload = (file) => {
  if (hasBlockedVideoExtension(file.name)) {
    return false;
  }

  return (
    SUPPORTED_VIDEO_TYPES.includes(file.type) ||
    hasSupportedVideoExtension(file.name)
  );
};

const prepareMediaFile = async (file) => {
  const isSupportedVideo = isSupportedVideoUpload(file);

  if (!SUPPORTED_IMAGE_TYPES.includes(file.type) && !isSupportedVideo) {
    throw new Error("Unsupported media type.");
  }

  if (isSupportedVideo) {
    if (file.size > MAX_VIDEO_SIZE) {
      throw new Error("Video must be smaller than 30MB");
    }

    return {
      file,
      fieldName: "video",
    };
  }

  if (file.size > MAX_IMAGE_SIZE) {
    throw new Error("Image must be smaller than 30MB");
  }

  if (file.type === "image/heic" || file.type === "image/heif") {
    const convertedBlob = await heic2any({
      blob: file,
      toType: "image/jpg",
      quality: 0.9,
    });

    return {
      file: new File([convertedBlob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
        type: "image/jpg",
        lastModified: Date.now(),
      }),
      fieldName: "image",
    };
  }

  return {
    file,
    fieldName: "image",
  };
};

// const preventDrag = (event) => {
//   event.preventDefault();
// };

const GalleryTile = memo(function GalleryTile({
  id,
  url,
  type,
  isUploading,
  isDeleting,
  isGenerating,
  selectedIndex,
  onToggle,
}) {
  const isSelected = selectedIndex !== -1;

  return (
    <ImageTile
      key={id}
      type="button"
      onClick={() =>
        !isUploading &&
        !isDeleting &&
        onToggle({
          id,
          isUploading,
          type,
          url,
        })
      }
      //   onDragStart={preventDrag}
      disabled={isGenerating || isUploading || isDeleting}
      aria-pressed={isSelected}
      aria-label={isSelected ? "Remove image from selection" : "Select image"}
    >
      {type === "video" ? (
        <TileVideo
          src={url}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
        />
      ) : (
        <TileImage
          src={url}
          alt="User gallery item"
          draggable={false}
          //   onDragStart={preventDrag}
        />
      )}
      <MediaIconWrapper>
        {type === "video" ? <VideoRecorderIcon /> : <ImageIcon />}
      </MediaIconWrapper>
      {isUploading ? (
        <UploadingOverlay>
          <l-line-spinner size="24" stroke="2" speed="1" color="#fff" />
        </UploadingOverlay>
      ) : isSelected ? (
        <SelectionIndicator>{selectedIndex + 1}</SelectionIndicator>
      ) : (
        <EmptySelectionIndicator />
      )}
    </ImageTile>
  );
});

const UserGalleryModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedImages = EMPTY_SELECTED_IMAGES,
  uploadedImagesCount = 0,
  generatePrice = DEFAULT_GENERATE_PRICE,
  maxSelectable = MAX_SELECTED_IMAGES,
  confirmLabel = "Generate",
  showPrice = true,
  overlayZIndex = 3100,
  showUploadTile = true,
  onOpenSettings,
  isConfirmLoading = false,
  allowedMediaTypes = ["image", "video"],
  onCreateImage,
}) => {
  const isCreateMode = typeof onCreateImage === "function";
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const gridViewportRef = useRef(null);
  const uploadingImagesRef = useRef([]);
  const deleteSelectionPendingRef = useRef(false);
  const galleryItems = useSelector(selectUserGallery);
  const page = useSelector(selectUserGalleryPage);
  const maxReached = useSelector(selectUserGalleryMaxReached);
  const loading = useSelector(selectUserGalleryPending);
  const isDeleting = useSelector(selectDeleteUserGalleryImagePending);
  const isUploading = useSelector(selectAddUserGalleryImagePending);
  const [addedImages, setAddedImages] = useState(selectedImages);
  const [uploadingImages, setUploadingImages] = useState([]);

  useLockBodyScroll(isOpen);

  useEffect(() => {
    uploadingImagesRef.current = uploadingImages;
  }, [uploadingImages]);

  useEffect(() => {
    return () => {
      uploadingImagesRef.current.forEach((image) => {
        URL.revokeObjectURL(image.previewUrl);
      });
    };
  }, []);

  useEffect(() => {
    setAddedImages(selectedImages);
  }, [selectedImages]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (page === 0 && !loading) {
      dispatch(fetchUserGallery({ page: 1 }));
    }
  }, [dispatch, isOpen, loading, page]);

  const normalizedAllowedMediaTypes = useMemo(
    () => new Set(allowedMediaTypes),
    [allowedMediaTypes],
  );
  const isVideoOnlyGallery =
    normalizedAllowedMediaTypes.size === 1 &&
    normalizedAllowedMediaTypes.has("video");
  const uploadLabel = isVideoOnlyGallery ? "Upload Video" : "Upload Image";
  const uploadEmptyLabel = isVideoOnlyGallery
    ? "Upload Videos"
    : "Upload Images";
  const emptyStateTitle = isVideoOnlyGallery
    ? "No videos yet"
    : "No images yet";
  const emptyStateDescription = isVideoOnlyGallery
    ? "Add videos to your gallery so you can access and reuse them anytime."
    : "Add images to your gallery so you can access and reuse them anytime.";
  const fileInputAccept = isVideoOnlyGallery
    ? VIDEO_FILE_ACCEPT.join(",")
    : [
        ...SUPPORTED_IMAGE_TYPES,
        ...(normalizedAllowedMediaTypes.has("video") ? VIDEO_FILE_ACCEPT : []),
      ].join(",");

  const visibleGalleryItems = useMemo(() => {
    return galleryItems
      .map((item) => {
        const mediaItem = getGalleryMediaItem(item);

        if (!mediaItem?.url) {
          return null;
        }

        return {
          ...mediaItem,
          id: item?.__uploadRequestId || mediaItem.url,
          isUploading: Boolean(item?.__uploading),
        };
      })
      .filter(
        (item) => item?.url && normalizedAllowedMediaTypes.has(item.type),
      );
  }, [galleryItems, normalizedAllowedMediaTypes]);
  const hasImages = visibleGalleryItems.length > 0;
  const showInitialLoading = loading && !hasImages;
  const totalGeneratePrice = Math.ceil(
    generatePrice * Math.max(addedImages.length, 1),
  );
  const isGenerating = isConfirmLoading;
  const selectedImageOrderMap = useMemo(
    () =>
      new Map(
        addedImages.map((imageUrl, index) => [getBaseUrl(imageUrl), index]),
      ),
    [addedImages],
  );

  const loadMoreGalleryItems = async () => {
    if (loading || maxReached || !isOpen) {
      return;
    }

    try {
      await dispatch(fetchUserGallery({ page: page + 1 })).unwrap();
    } catch (error) {
      toast.error(error.message || "Failed to load more gallery items");
    }
  };

  const handleGridScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollHeight - scrollTop - clientHeight < 96) {
      loadMoreGalleryItems();
    }
  };

  const handleToggle = useCallback(
    (mediaItem) => {
      if (isGenerating || isDeleting || deleteSelectionPendingRef.current) {
        return;
      }

      const url = mediaItem.url;
      const baseUrl = getBaseUrl(url);

      setAddedImages((prev) => {
        const exists = prev.some(
          (imageUrl) => getBaseUrl(imageUrl) === baseUrl,
        );

        if (exists) {
          return prev.filter((imageUrl) => getBaseUrl(imageUrl) !== baseUrl);
        }

        if (maxSelectable === 1) {
          return [url];
        }

        if (uploadedImagesCount + prev.length >= maxSelectable) {
          toast.error(`Please select max ${maxSelectable} images`);
          return prev;
        }

        return [...prev, url];
      });
    },
    [isDeleting, isGenerating, maxSelectable, uploadedImagesCount],
  );

  const handleGenerate = async () => {
    if (uploadedImagesCount + addedImages.length > maxSelectable) {
      toast.error(`Please select max ${maxSelectable} images`);
      return;
    }

    if (!addedImages.length) {
      toast.error("Please select at least one item");
      return;
    }

    try {
      const selectedMediaItems = addedImages
        .map((selectedUrl) =>
          visibleGalleryItems.find(
            (item) => getBaseUrl(item.url) === getBaseUrl(selectedUrl),
          ),
        )
        .filter(Boolean);

      const shouldClose = await onConfirm(selectedMediaItems);

      if (shouldClose !== false) {
        handleClose();
      }
    } catch (error) {
      toast.error(error.message || "Failed to start generation");
    }
  };

  const handleClose = () => {
    setAddedImages(selectedImages);
    onClose();
  };

  const handleDeleteSelected = async () => {
    if (!addedImages.length || deleteSelectionPendingRef.current) {
      return;
    }

    const selectedImagesToDelete = addedImages;
    deleteSelectionPendingRef.current = true;
    setAddedImages([]);

    try {
      await dispatch(
        deleteUserGalleryImage({ urls: selectedImagesToDelete }),
      ).unwrap();
    } catch (error) {
      setAddedImages(selectedImagesToDelete);
      toast.error(error.message || "Failed to delete selected images");
    } finally {
      deleteSelectionPendingRef.current = false;
    }
  };

  const openFilePicker = () => {
    if (isGenerating) {
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (!selectedFiles.length) {
      return;
    }

    if (selectedFiles.length > MAX_UPLOAD_BATCH) {
      toast.error(
        `You can upload up to ${MAX_UPLOAD_BATCH} ${
          isVideoOnlyGallery ? "videos" : "images"
        } at once`,
      );
    }

    const filteredFiles = selectedFiles.filter((file) => {
      const isVideo = isSupportedVideoUpload(file);
      const mediaType = isVideo ? "video" : "image";

      return normalizedAllowedMediaTypes.has(mediaType);
    });

    if (!filteredFiles.length) {
      toast.error(
        isVideoOnlyGallery
          ? "Please select a valid MP4 or MOV video file"
          : "Please select a valid image file",
      );
      return;
    }

    if (filteredFiles.length !== selectedFiles.length) {
      toast.error(
        isVideoOnlyGallery
          ? "Only MP4 and MOV uploads are allowed here"
          : "Only image uploads are allowed here",
      );
    }

    if (filteredFiles.length > MAX_UPLOAD_BATCH) {
      toast.error(
        `You can upload up to ${MAX_UPLOAD_BATCH} ${
          isVideoOnlyGallery ? "videos" : "images"
        } at once`,
      );
    }

    const filesToUpload = filteredFiles.slice(0, MAX_UPLOAD_BATCH);
    const preparedUploads = await Promise.all(
      filesToUpload.map(async (selectedFile) => {
        const preparedFile = await prepareMediaFile(selectedFile);
        const pendingUpload = createPendingUpload(preparedFile.file);

        return {
          ...preparedFile,
          pendingUpload,
        };
      }),
    ).catch((error) => {
      toast.error(error.message || "Failed to add image to gallery");
      return null;
    });

    if (!preparedUploads?.length) {
      return;
    }

    setUploadingImages((prev) => [
      ...preparedUploads.map(({ pendingUpload }) => pendingUpload),
      ...prev,
    ]);

    const uploadResults = await Promise.allSettled(
      preparedUploads.map(async ({ file, fieldName, pendingUpload }) => {
        try {
          await dispatch(
            addUserGalleryImage({
              file,
              fieldName,
              previewUrl: pendingUpload.previewUrl,
              mediaType: pendingUpload.type,
              clientUploadId: pendingUpload.id,
            }),
          ).unwrap();
        } finally {
          setUploadingImages((prev) =>
            prev.filter((image) => image.id !== pendingUpload.id),
          );
          URL.revokeObjectURL(pendingUpload.previewUrl);
        }
      }),
    );

    const successfulUploads = uploadResults.filter(
      (result) => result.status === "fulfilled",
    ).length;
    const failedUpload = uploadResults.find(
      (result) => result.status === "rejected",
    );

    if (successfulUploads) {
      toast.success(
        successfulUploads === 1
          ? `${isVideoOnlyGallery ? "Video" : "Image"} uploaded to gallery`
          : `${successfulUploads} ${
              isVideoOnlyGallery ? "videos" : "images"
            } uploaded to gallery`,
      );
    }

    if (failedUpload?.status === "rejected") {
      toast.error(
        failedUpload.reason?.message ||
          "Failed to add one or more images to gallery",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      customOverlayStyles={{ zIndex: overlayZIndex }}
    >
      <ModalContainer data-allow-rapid-touch>
        <ModalContent>
          <Header>
            <Title>Gallery</Title>
            <HeaderActions>
              {hasImages && (
                <IconButton
                  $danger
                  type="button"
                  aria-label="Clear selected gallery images"
                  disabled={!addedImages.length || isDeleting || isGenerating}
                  onClick={handleDeleteSelected}
                >
                  <TrashSvg width={16} height={16} />
                </IconButton>
              )}
              <IconButton
                type="button"
                aria-label="Close gallery"
                onClick={handleClose}
              >
                <CloseSvg width={24} height={24} />
              </IconButton>
            </HeaderActions>
          </Header>

          {showInitialLoading ? (
            <GridViewport>
              <Grid>
                {Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonTile key={`user-gallery-skeleton-${index}`} />
                ))}
              </Grid>
            </GridViewport>
          ) : hasImages ? (
            <>
              <GridViewport ref={gridViewportRef} onScroll={handleGridScroll}>
                <Grid>
                  {isCreateMode ? (
                    <UploadTile
                      type="button"
                      onClick={onCreateImage}
                      aria-label="Create Image"
                      disabled={isGenerating}
                    >
                      <MagicStickSvg width={24} height={24} />
                      <UploadLabel>Create Image</UploadLabel>
                    </UploadTile>
                  ) : (
                    showUploadTile && (
                      <UploadTile
                        type="button"
                        onClick={openFilePicker}
                        aria-label={uploadLabel}
                        disabled={
                          isUploading ||
                          uploadingImages.length > 0 ||
                          isGenerating
                        }
                      >
                        <ImageUserPlusSvg width={24} height={24} />
                        <UploadLabel>{uploadLabel}</UploadLabel>
                      </UploadTile>
                    )
                  )}

                  {visibleGalleryItems.map((mediaItem) => {
                    const { url, type, isUploading, id } = mediaItem;
                    const selectedIndex =
                      selectedImageOrderMap.get(getBaseUrl(url)) ?? -1;

                    return (
                      <GalleryTile
                        key={id}
                        id={id}
                        url={url}
                        type={type}
                        isUploading={isUploading}
                        isDeleting={isDeleting}
                        isGenerating={isGenerating}
                        selectedIndex={selectedIndex}
                        onToggle={handleToggle}
                      />
                    );
                  })}
                </Grid>
              </GridViewport>

              <Footer>
                {onOpenSettings && (
                  <FooterIconButton
                    type="button"
                    aria-label="Open generation settings"
                    onClick={() => onOpenSettings()}
                  >
                    <SettingsSvg />
                  </FooterIconButton>
                )}

                <GenerateButton
                  type="button"
                  onClick={handleGenerate}
                  disabled={
                    loading || isDeleting || isUploading || isConfirmLoading
                  }
                >
                  {isGenerating ? (
                    <l-line-spinner
                      size="18"
                      stroke="1.5"
                      speed="1.2"
                      color="#0f2c32"
                    />
                  ) : (
                    <GenerateButtonText>{confirmLabel}</GenerateButtonText>
                  )}
                  {showPrice && !isGenerating && (
                    <Cost>
                      <GradientStarIcon />
                      {totalGeneratePrice}
                    </Cost>
                  )}
                </GenerateButton>
              </Footer>
            </>
          ) : (
            <EmptyState>
              <EmptyStateIcon>
                <ImageIcon />
              </EmptyStateIcon>
              <EmptyStateContent>
                <EmptyStateTitle>{emptyStateTitle}</EmptyStateTitle>
                <EmptyStateDescription>
                  {emptyStateDescription}
                </EmptyStateDescription>
              </EmptyStateContent>
              <GenerateButton
                type="button"
                onClick={isCreateMode ? onCreateImage : openFilePicker}
                disabled={
                  isCreateMode
                    ? isGenerating
                    : isUploading || uploadingImages.length > 0
                }
                style={{ flex: "unset", minWidth: "182px" }}
              >
                <GenerateButtonText>
                  {isCreateMode ? "Create Image" : uploadEmptyLabel}
                </GenerateButtonText>
              </GenerateButton>
            </EmptyState>
          )}
          <input
            type="file"
            ref={fileInputRef}
            accept={fileInputAccept}
            multiple
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default UserGalleryModal;
